import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CheckoutField, CheckoutPage } from '../types';

interface CheckoutStore {
  currentPage: CheckoutPage | null;
  publishedPages: CheckoutPage[];
  fields: CheckoutField[];
  setCurrentPage: (page: CheckoutPage) => void;
  updatePageSettings: (settings: Partial<CheckoutPage>) => void;
  addField: (field: CheckoutField) => void;
  removeField: (id: string) => void;
  reorderFields: (fields: CheckoutField[]) => void;
  publishPage: (page: CheckoutPage) => void;
  updatePublishedPage: (id: string, updates: Partial<CheckoutPage>) => void;
  archivePage: (id: string) => void;
  deletePage: (id: string) => void;
  getPublishedPage: (id: string) => CheckoutPage | null;
}

const defaultCheckoutPage: CheckoutPage = {
  id: 'demo-page',
  name: 'Demo Checkout Page',
  fields: [],
  productId: 'test-product-1',
  displayName: 'Premium Course',
  displayDescription: 'Complete course with all materials and lifetime access',
  templateId: 'modern-split',
  theme: {
    primaryColor: '#1E293B',
    fontFamily: 'Inter',
  },
  active: true,
  archived: false,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  slug: 'demo-checkout',
  showCouponOption: true
};

const initialState = {
  currentPage: null,
  publishedPages: [defaultCheckoutPage],
  fields: [],
};

export const useCheckoutStore = create<CheckoutStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setCurrentPage: (page) => set({ currentPage: page }),
      updatePageSettings: (settings) => set((state) => ({
        currentPage: state.currentPage ? { ...state.currentPage, ...settings } : null,
        publishedPages: state.currentPage 
          ? state.publishedPages.map(p => 
              p.id === state.currentPage?.id 
                ? { ...p, ...settings }
                : p
            )
          : state.publishedPages
      })),
      addField: (field) => set((state) => ({ fields: [...state.fields, field] })),
      removeField: (id) => set((state) => ({
        fields: state.fields.filter((field) => field.id !== id)
      })),
      reorderFields: (fields) => set({ fields }),
      publishPage: (page) => set((state) => ({
        publishedPages: [...state.publishedPages.filter(p => p.id !== page.id), {
          ...page,
          active: true,
          archived: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }]
      })),
      updatePublishedPage: (id, updates) => set((state) => ({
        publishedPages: state.publishedPages.map(page =>
          page.id === id ? { 
            ...page, 
            ...updates,
            updatedAt: new Date().toISOString()
          } : page
        )
      })),
      archivePage: (id) => set((state) => ({
        publishedPages: state.publishedPages.map(page =>
          page.id === id ? { 
            ...page, 
            archived: true, 
            active: false,
            updatedAt: new Date().toISOString()
          } : page
        )
      })),
      deletePage: (id) => set((state) => ({
        publishedPages: state.publishedPages.filter(page => page.id !== id)
      })),
      getPublishedPage: (id) => {
        const page = get().publishedPages.find(p => p.id === id || p.slug === id);
        return page || null;
      },
    }),
    {
      name: 'checkout-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        publishedPages: state.publishedPages,
        currentPage: state.currentPage,
      }),
      version: 2,
      migrate: (persistedState: any, version: number) => {
        if (version === 1) {
          return {
            ...initialState,
            ...persistedState,
          };
        }
        return persistedState;
      },
    }
  )
);